import { cn } from "@/utils";
import { Chip, Tooltip } from "@heroui/react";
import dayjs from "dayjs";

const MonthDayBlock = ({
  date,
  onDateClick,
  gap = "0px",
  disableOnHolidays,
  isHoliday,
  isOutsideMonth = false,
  isUnavailable = false,
  badge,
  ...props
}: {
  date: string;
  onDateClick: () => void;
  gap?: string;
  disableOnHolidays?: boolean;
  isHoliday?: string;
  isOutsideMonth?: boolean;
  /** Hatched like a closed holiday, without the holiday's name. */
  isUnavailable?: boolean;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const dateObject = dayjs(date);
  const dayNumber = dateObject.date();
  const isToday = dateObject.isSame(dayjs(), "day");

  return (
    <div
      className={cn(
        "flex h-40 flex-col overflow-hidden border border-divider px-px py-1 md:px-1",
        {
          "bg-default-100":
            isOutsideMonth && !disableOnHolidays && !isUnavailable,
          "bg-[repeating-linear-gradient(159.5deg,transparent,transparent_8px,var(--divider)_8px,var(--divider)_18px)]":
            disableOnHolidays || isUnavailable,
        },
      )}
    >
      <div className="flex flex-col-reverse justify-between gap-1 md:flex-row md:items-center">
        <button
          type="button"
          onClick={onDateClick}
          className="flex h-5 items-center justify-center rounded-xl p-2 transition-colors hover:bg-default-100 md:justify-start"
        >
          <p
            className={cn("text-sm font-semibold", {
              "text-foreground": !isOutsideMonth,
              "text-default-400": isOutsideMonth,
              "text-[#5a9f6c]": isToday,
            })}
          >
            {dayNumber}
          </p>
        </button>

        <div className="flex max-w-[70%] min-w-0 items-center justify-center gap-1 md:justify-end">
          {badge}

          {isHoliday && (
            <Tooltip
              content={isHoliday}
              classNames={{
                content: "bg-neutral text-[#fafafa]",
                arrow: "bg-neutral",
              }}
              showArrow
            >
              <Chip
                size="sm"
                className="text-truncate max-w-full bg-neutral text-xs text-[#fafafa]"
              >
                {isHoliday}
              </Chip>
            </Tooltip>
          )}
        </div>
      </div>
      <div
        className={cn("flex w-full flex-col overflow-hidden pt-1", {
          "opacity-50": isOutsideMonth,
        })}
        style={{
          gap: gap,
        }}
      >
        {props.children}
      </div>
    </div>
  );
};

export default MonthDayBlock;
